export const isUnresolvedGroupedResult = (result) => result?.result_key_resolved_from === "unresolved_grouped_combination";
export const isResolvedGroupedResult = (result) => result?.result_key_resolved_from === "grouped_combination" && !!result?.result_mapping_entry;

export function getResultPresentation(result, layout = {}) {
  const unresolvedGrouped = isUnresolvedGroupedResult(result);
  const resolvedGrouped = isResolvedGroupedResult(result);
  const hasMapping = !!result?.result_mapping_entry;
  // Big Five traits and letter type codes have no "dominant" winner, so the legacy
  // dominant-profile hero never fits them.
  const ownProfile = !!(result?.big_five || result?.type_code);
  return { unresolvedGrouped, resolvedGrouped, useUniversalHero: hasMapping && layout.show_hero !== false, suppressLegacyHero: unresolvedGrouped || resolvedGrouped || ownProfile };
}

export function hasRenderableResultContent(value) {
  if (typeof value !== "string") return value !== null && value !== undefined;
  return value
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;|&#160;|&#x0*a0;/gi, " ")
    .trim().length > 0;
}

function localizedField(source, field, lang, suffix = "") {
  const preferred = source?.[`${field}_${lang}${suffix}`];
  const fallback = lang === "en" ? source?.[`${field}_id${suffix}`] : source?.[`${field}_en${suffix}`];
  return preferred || fallback || "";
}

export function getResultAccessState({ mode = "free", entitlement = {}, accessToken = "" } = {}) {
  const isPaid = mode === "paid" || entitlement?.required === true;
  const unlocked = isPaid && !!accessToken && entitlement?.granted === true;
  return {
    isPaid,
    unlocked,
    paidContentLocked: isPaid && !unlocked,
    premiumContentVisible: !isPaid || unlocked,
  };
}

export function getLocalizedSafeResultContent(result, lang = "id") {
  const mapping = result?.result_mapping_entry || {};
  const primary = result?.primary_dimension || result?.dominant_dimension || {};
  const mappingTitle = localizedField(mapping, "title", lang);
  const primaryName = localizedField(primary, "name", lang);
  return {
    title: mappingTitle || primaryName,
    subtitle: localizedField(mapping, "subtitle", lang),
    badge: localizedField(mapping, "badge", lang),
    reflection: localizedField(mapping, "reflection", lang),
    image: localizedField(mapping, "hero_image", lang, "_url") || "",
    color: mapping.color || "",
  };
}

export function getSafeResultBlocks(result, layout = {}) {
  const mapping = result?.result_mapping_entry || {};
  const primary = result?.primary_dimension || result?.dominant_dimension || {};
  const heroFields = [
    mapping.title_id,
    mapping.title_en,
    mapping.subtitle_id,
    mapping.subtitle_en,
    mapping.badge_id,
    mapping.badge_en,
    mapping.hero_image_id_url,
    mapping.hero_image_en_url,
    primary.name_id,
    primary.name_en,
  ];
  const learnMoreFields = [
    mapping.subtitle_id,
    mapping.subtitle_en,
    mapping.reflection_id,
    mapping.reflection_en,
  ];
  return {
    showHero: layout.show_hero !== false && heroFields.some(hasRenderableResultContent),
    showLearnMore: layout.show_reflection !== false && learnMoreFields.some(hasRenderableResultContent),
  };
}

// Optional Learn More lists stored on a result mapping entry's `extras`, one item per line.
export const LEARN_MORE_EXTRA_FIELDS = [
  { key: "strengths", label_id: "Kekuatanmu", label_en: "Your strengths" },
  { key: "work_environments", label_id: "Lingkungan kerja yang cocok", label_en: "Work environments that suit you" },
  { key: "majors", label_id: "Contoh jurusan / studi lanjut", label_en: "Example study paths" },
  { key: "careers", label_id: "Contoh karier", label_en: "Example careers" },
];

// The instrument's own list headings, or the defaults when it has never set any.
export function learnMoreLists(instrument) {
  return Array.isArray(instrument?.learn_more_lists) ? instrument.learn_more_lists : LEARN_MORE_EXTRA_FIELDS;
}

export function getLearnMoreExtras(entry, lang = "id", lists = LEARN_MORE_EXTRA_FIELDS) {
  const extras = entry?.extras;
  if (!extras || typeof extras !== "object") return [];
  return lists.map((field) => {
    const raw = localizedField(extras, field.key, lang);
    const items = (typeof raw === "string" ? raw.split("\n") : Array.isArray(raw) ? raw : [])
      .map((item) => String(item).trim())
      .filter(Boolean);
    const label = lang === "en" ? (field.label_en || field.label_id) : (field.label_id || field.label_en);
    return { key: field.key, label: label || "", items };
  }).filter((section) => section.items.length > 0);
}
